import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Play, Square, Plus, Trash2, X, RefreshCw, ExternalLink,
  Search, TrendingUp, TrendingDown, Radio, AlertCircle, CheckCircle,
  Wifi, WifiOff, Download,
} from 'lucide-react'
import { watchlistAPI, streamingAPI, isApiPaused } from '../services/api'
import { useWebSocket } from '../contexts/WebSocketContext'
import { useAuth } from '../contexts/AuthContext'
import { openTradingView } from '../utils/tradingView'
import toast from 'react-hot-toast'

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, d = 2) =>
  n == null || isNaN(n) ? '—' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'

// ── Instrument search autocomplete ────────────────────────────────────────────
const InstrumentSearch = ({ onAdd }) => {
  const [query,   setQuery]   = useState('')
  const [results, setResults] = useState([])
  const [open,    setOpen]    = useState(false)
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  useEffect(() => {
    if (query.length < 2) { setResults([]); setOpen(false); return }
    setLoading(true)
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/watchlist/instruments?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        const data = await res.json()
        setResults((data.instruments || []).slice(0, 10))
        setOpen(true)
      } catch { setResults([]) }
      finally { setLoading(false) }
    }, 250)
    return () => clearTimeout(t)
  }, [query])

  const pick = (inst) => { onAdd(inst); setQuery(''); setResults([]); setOpen(false) }

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search and add symbol…"
          className="input pl-9 w-full text-sm"
        />
        {loading && <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 max-h-60 overflow-y-auto">
          {results.map((r, i) => (
            <button
              key={i}
              onClick={() => pick(r)}
              className="w-full text-left px-3 py-2.5 hover:bg-primary-50 transition-colors flex items-center justify-between"
            >
              <span className="font-medium text-sm text-gray-800">{r.tradingsymbol}</span>
              <span className="text-xs text-gray-400">{r.exchange || 'NSE'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const Streaming = () => {
  const { user } = useAuth()
  const {
    connected: wsConnected, symbolTicks, subscribe,
    kiteStreamStopped, kiteStreamStopReason, restartKiteStream,
    streamPaused, streamPausedReason, clearStreamPaused,
  } = useWebSocket()

  // Streaming status
  const [streamStatus, setStreamStatus] = useState({
    running: false, connected: false, subscribedCount: 0, provider: 'kite', lastTickAt: null,
  })
  const [starting, setStarting] = useState(false)
  const [stopping, setStopping] = useState(false)

  // Watchlists
  const [watchlists,  setWatchlists]  = useState([])
  const [activeWlId,  setActiveWlId]  = useState(null)
  const [wlLoading,       setWlLoading]       = useState(true)
  const [showNewWl,       setShowNewWl]       = useState(false)
  const [newWlName,       setNewWlName]       = useState('')
  const [creatingWl,      setCreatingWl]      = useState(false)
  const [importingNifty,  setImportingNifty]  = useState(false)

  // Kite REST quotes (used when provider = 'kite')
  const [kiteQuotes,     setKiteQuotes]     = useState({})
  const [quotesLoading,  setQuotesLoading]  = useState(false)

  const activeWl   = watchlists.find(w => w._id === activeWlId) || watchlists[0] || null
  const isAngelOne = streamStatus.provider === 'angelone'

  // ── Initial load ──────────────────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    try {
      const res = await streamingAPI.getStatus()
      setStreamStatus(prev => ({ ...prev, ...res.data }))
    } catch { /* ignore */ }
  }, [])

  const fetchWatchlists = useCallback(async () => {
    setWlLoading(true)
    try {
      const res = await watchlistAPI.getWatchlists()
      const data = res.data || []
      setWatchlists(data)
      if (data.length && !activeWlId) setActiveWlId(data[0]._id)
    } catch { toast.error('Failed to load watchlists') }
    finally { setWlLoading(false) }
  }, [activeWlId])

  useEffect(() => {
    fetchStatus()
    fetchWatchlists()
    // 30 s is plenty to reflect streaming state — was 10 s (too aggressive)
    const t = setInterval(() => { if (!isApiPaused()) fetchStatus() }, 30_000)
    return () => clearInterval(t)
  }, []) // eslint-disable-line

  // ── Subscribe Kite WebSocket on watchlist change ───────────────────────────
  useEffect(() => {
    if (!activeWl || isAngelOne) return
    const tokens = activeWl.instruments.map(i => i.instrumentToken).filter(Boolean)
    if (tokens.length) subscribe(tokens)
  }, [activeWl?._id, isAngelOne]) // eslint-disable-line

  // ── Kite REST quotes ──────────────────────────────────────────────────────
  const fetchKiteQuotes = useCallback(async () => {
    if (!activeWl || isAngelOne || !activeWl.instruments.length) return
    setQuotesLoading(true)
    try {
      const res = await watchlistAPI.getQuotes(activeWl._id)
      setKiteQuotes(res.data?.data || {})
    } catch { /* non-critical */ }
    finally { setQuotesLoading(false) }
  }, [activeWl?._id, isAngelOne]) // eslint-disable-line

  useEffect(() => { fetchKiteQuotes() }, [activeWl?._id, isAngelOne]) // eslint-disable-line

  // ── Start / Stop streaming ────────────────────────────────────────────────
  const handleStart = async () => {
    setStarting(true)
    try {
      const res = await streamingAPI.start()
      setStreamStatus(prev => ({ ...prev, ...res.data.status, running: true }))
      toast.success(`AngelOne stream started — ${res.data.symbolCount} symbols`)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start streaming')
    } finally {
      setStarting(false)
      fetchStatus()
    }
  }

  const handleStop = async () => {
    setStopping(true)
    try {
      await streamingAPI.stop()
      setStreamStatus(prev => ({ ...prev, running: false, connected: false, subscribedCount: 0 }))
      toast.success('Streaming stopped')
    } catch { toast.error('Failed to stop') }
    finally { setStopping(false) }
  }

  // ── Watchlist CRUD ────────────────────────────────────────────────────────
  const createWatchlist = async () => {
    if (!newWlName.trim()) return
    setCreatingWl(true)
    try {
      const res = await watchlistAPI.createWatchlist(newWlName.trim())
      setWatchlists(prev => [...prev, res.data])
      setActiveWlId(res.data._id)
      setNewWlName(''); setShowNewWl(false)
      toast.success(`"${res.data.name}" created`)
    } catch { toast.error('Failed to create watchlist') }
    finally { setCreatingWl(false) }
  }

  const deleteWatchlist = async (wl) => {
    if (!window.confirm(`Delete "${wl.name}"?`)) return
    try {
      await watchlistAPI.deleteWatchlist(wl._id)
      const remaining = watchlists.filter(w => w._id !== wl._id)
      setWatchlists(remaining)
      if (activeWlId === wl._id) setActiveWlId(remaining[0]?._id || null)
      toast.success('Watchlist deleted')
    } catch { toast.error('Failed to delete watchlist') }
  }

  const importNifty500 = async () => {
    setImportingNifty(true)
    try {
      const res = await watchlistAPI.createNifty500Watchlist()
      toast.success(res.data.message)
      await fetchWatchlists()
      // If AngelOne is running, restart so new symbols get subscribed
      if (streamStatus.running) {
        await streamingAPI.stop()
        setTimeout(() => handleStart(), 500)
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to import'
      toast.error(msg)
    } finally {
      setImportingNifty(false)
    }
  }

  const addInstrument = async (inst) => {
    if (!activeWl) { toast.error('Select a watchlist first'); return }
    if (activeWl.instruments.some(i => i.symbol === inst.tradingsymbol)) { toast('Already in list'); return }
    try {
      const res = await watchlistAPI.addInstrument(activeWl._id, {
        symbol:          inst.tradingsymbol,
        exchange:        inst.exchange || 'NSE',
        instrumentToken: String(inst.instrument_token || inst.instrumentToken || ''),
      })
      setWatchlists(prev => prev.map(w => w._id === activeWl._id ? res.data : w))
      if (streamStatus.running && isAngelOne) {
        streamingAPI.subscribe([inst.tradingsymbol]).catch(() => {})
      }
      toast.success(`${inst.tradingsymbol} added`)
    } catch { toast.error('Failed to add') }
  }

  const removeInstrument = async (instrumentId, symbol) => {
    if (!activeWl) return
    try {
      const res = await watchlistAPI.removeInstrument(activeWl._id, instrumentId)
      setWatchlists(prev => prev.map(w => w._id === activeWl._id ? res.data : w))
      toast.success(`${symbol} removed`)
    } catch { toast.error('Failed to remove') }
  }

  // ── Tick data (merge AngelOne WS + Kite REST) ─────────────────────────────
  const getTickForSymbol = (symbol) => {
    const wsTick = symbolTicks?.[symbol]
    if (wsTick) return wsTick
    if (isAngelOne) return null
    const q = kiteQuotes[`NSE:${symbol}`] || kiteQuotes[symbol]
    if (!q) return null
    return {
      symbol, ltp: q.last_price,
      open: q.ohlc?.open, high: q.ohlc?.high, low: q.ohlc?.low, close: q.ohlc?.close,
      change: q.net_change ? (q.net_change / (q.ohlc?.close || 1)) * 100 : (q.change || 0),
      volume: q.volume, source: 'kite',
    }
  }

  const instruments = activeWl?.instruments || []
  const [wlSearch, setWlSearch] = useState('')

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Radio className="w-6 h-6 text-primary-500" /> Streaming
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Live market data &amp; watchlist management</p>
        </div>

        {/* Stream control */}
        <div className="flex items-center gap-3 flex-wrap">

          {/* Provider + status badge */}
          <div className={`flex items-center gap-2 rounded-lg px-3 py-2 ${
            !isAngelOne && kiteStreamStopped ? 'bg-danger-50 border border-danger-200' : 'bg-gray-100'
          }`}>
            <span className={`w-2.5 h-2.5 rounded-full ${
              !isAngelOne && kiteStreamStopped ? 'bg-danger-500' :
              streamStatus.running && streamStatus.connected ? 'bg-success-500 animate-pulse' :
              streamStatus.running ? 'bg-yellow-400 animate-pulse' : 'bg-gray-300'
            }`} />
            <span className={`text-sm font-medium ${
              !isAngelOne && kiteStreamStopped ? 'text-danger-700' : 'text-gray-700'
            }`}>
              {isAngelOne ? 'AngelOne' : 'Kite'}
              {!isAngelOne && kiteStreamStopped && ' · stopped'}
            </span>
            {streamStatus.running && (
              <span className="text-xs text-gray-500">· {streamStatus.subscribedCount} live</span>
            )}
          </div>

          {/* The one button */}
          {isAngelOne ? (
            streamStatus.running ? (
              <button
                onClick={handleStop}
                disabled={stopping}
                className="flex items-center gap-2 bg-danger-600 hover:bg-danger-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-lg transition-colors"
              >
                {stopping ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
                Stop Stream
              </button>
            ) : (
              <button
                onClick={handleStart}
                disabled={starting}
                className="flex items-center gap-2 bg-success-600 hover:bg-success-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-lg transition-colors"
              >
                {starting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                {starting ? 'Connecting…' : 'Start Stream'}
              </button>
            )
          ) : kiteStreamStopped ? (
            // Kite 403 — show restart button
            <button
              onClick={restartKiteStream}
              className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-5 py-2 rounded-lg transition-colors"
            >
              <Play className="w-4 h-4" /> Start Streaming
            </button>
          ) : (
            <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              {wsConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4 text-gray-400" />}
              Kite WebSocket {wsConnected ? 'connected' : 'offline'}
            </div>
          )}
        </div>
      </div>

      {/* Kite stream stopped (403) banner — only for Kite provider */}
      {!isAngelOne && kiteStreamStopped && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-2.5">
            <WifiOff className="w-5 h-5 text-danger-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-danger-800">Kite stream disconnected</p>
              <p className="text-xs text-danger-600 mt-0.5">{kiteStreamStopReason}</p>
              <p className="text-xs text-danger-500 mt-1">
                If your Kite session expired, go to{' '}
                <a href="/settings" className="underline font-medium">Settings</a>
                {' '}and reconnect, then click <strong>Start Streaming</strong>.
              </p>
            </div>
          </div>
          <button
            onClick={restartKiteStream}
            className="shrink-0 flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            <Play className="w-3.5 h-3.5" /> Start Streaming
          </button>
        </div>
      )}

      {/* Circuit-breaker pause banner — AngelOne (or Kite) failed 3× in 15 s */}
      {streamPaused && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Streaming paused — circuit breaker triggered</p>
              <p className="text-xs text-amber-700 mt-0.5">{streamPausedReason}</p>
              <p className="text-xs text-amber-600 mt-1">
                Too many connection failures in a short window. Check credentials in{' '}
                <a href="/settings" className="underline font-medium">Settings</a>
                , then click <strong>Start Stream</strong> when ready.
              </p>
            </div>
          </div>
          <button
            onClick={() => { clearStreamPaused(); handleStart() }}
            disabled={starting}
            className="shrink-0 flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
          >
            <Play className="w-3.5 h-3.5" /> Restart
          </button>
        </div>
      )}

      {/* Warning: AngelOne creds missing */}
      {isAngelOne && !streamStatus.running && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2.5 text-sm text-amber-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            AngelOne is the active provider. Configure credentials in{' '}
            <a href="/settings" className="underline font-medium">Settings → Streaming</a>
            , then click <strong>Start Stream</strong>.
          </span>
        </div>
      )}

      {/* Live banner */}
      {streamStatus.running && (
        <div className="bg-success-50 border border-success-200 rounded-lg px-4 py-2 flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-success-700 font-semibold">
            <Radio className="w-3.5 h-3.5 animate-pulse" /> Live
          </div>
          <span className="text-success-700">{streamStatus.subscribedCount} symbols</span>
          {streamStatus.lastTickAt && (
            <span className="text-success-600">Last tick {fmtTime(streamStatus.lastTickAt)}</span>
          )}
          {!wsConnected && (
            <span className="text-amber-600 ml-auto">⚠ Reconnecting WebSocket…</span>
          )}
        </div>
      )}

      {/* ── Watchlist panel ── */}
      <div className="card p-0 overflow-hidden">

        {/* Watchlist tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200 bg-gray-50 px-4 pt-2.5 flex-wrap">
          {watchlists.map(wl => (
            <div
              key={wl._id}
              onClick={() => setActiveWlId(wl._id)}
              className={`group flex items-center gap-1 px-3 py-2 rounded-t-lg text-sm font-medium cursor-pointer border-b-2 transition-all ${
                activeWlId === wl._id
                  ? 'bg-white border-primary-500 text-primary-700'
                  : 'border-transparent text-gray-500 hover:bg-white/70 hover:text-gray-700'
              }`}
            >
              {wl.name}
              <span className="text-xs text-gray-400 ml-0.5">({wl.instruments?.length || 0})</span>
              <button
                onClick={e => { e.stopPropagation(); deleteWatchlist(wl) }}
                className="ml-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-danger-500 transition-opacity"
                title="Delete"
              ><X className="w-3 h-3" /></button>
            </div>
          ))}

          {showNewWl ? (
            <div className="flex items-center gap-1.5 mb-0.5 px-2 py-1 bg-white rounded-lg border border-primary-200">
              <input
                autoFocus
                value={newWlName}
                onChange={e => setNewWlName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') createWatchlist(); if (e.key === 'Escape') setShowNewWl(false) }}
                placeholder="List name"
                className="text-sm outline-none w-28"
              />
              <button onClick={createWatchlist} disabled={creatingWl} className="text-primary-600">
                {creatingWl ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => setShowNewWl(false)} className="text-gray-400"><X className="w-3 h-3" /></button>
            </div>
          ) : (
            <button
              onClick={() => setShowNewWl(true)}
              className="flex items-center gap-1 px-2.5 py-2 text-sm text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> New list
            </button>
          )}

          <div className="ml-auto flex items-center gap-2 mb-0.5">
            {/* Import Nifty 500 — always shown, creates/refreshes the watchlist */}
            <button
              onClick={importNifty500}
              disabled={importingNifty}
              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 border border-primary-200 hover:border-primary-300 bg-primary-50 hover:bg-primary-100 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-60"
              title="Creates/refreshes NIFTY 500 watchlist with 501 correct symbols for AngelOne"
            >
              {importingNifty
                ? <RefreshCw className="w-3 h-3 animate-spin" />
                : <Download className="w-3 h-3" />}
              Nifty 500
            </button>

            {!isAngelOne && (
              <button
                onClick={fetchKiteQuotes}
                disabled={quotesLoading}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <RefreshCw className={`w-3 h-3 ${quotesLoading ? 'animate-spin' : ''}`} />
                Refresh quotes
              </button>
            )}
          </div>
        </div>

        {/* Search bar */}
        <div className="px-4 py-3 border-b border-gray-100">
          <InstrumentSearch onAdd={addInstrument} />
        </div>

        {/* Table */}
        {wlLoading ? (
          <div className="py-14 text-center text-sm text-gray-400">Loading…</div>
        ) : !activeWl ? (
          <div className="py-14 text-center text-sm text-gray-400">
            <p>No watchlists yet.</p>
            <button onClick={() => setShowNewWl(true)} className="mt-2 text-primary-600 hover:underline text-sm">
              Create your first watchlist
            </button>
          </div>
        ) : instruments.length === 0 ? (
          <div className="py-14 text-center text-sm text-gray-400">
            No instruments. Search above to add stocks.
          </div>
        ) : (
          <>
          {/* Symbol filter bar */}
          {instruments.length > 5 && (
            <div className="relative mb-2">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input
                value={wlSearch} onChange={e => setWlSearch(e.target.value)}
                placeholder="Filter symbols…" className="input pl-8 py-1.5 text-sm w-40"
              />
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-4 py-2.5">Symbol</th>
                  <th className="text-right px-4 py-2.5">LTP</th>
                  <th className="text-right px-4 py-2.5">Open</th>
                  <th className="text-right px-4 py-2.5">High</th>
                  <th className="text-right px-4 py-2.5">Low</th>
                  <th className="text-right px-4 py-2.5">Change %</th>
                  <th className="text-right px-4 py-2.5">Volume</th>
                  <th className="text-center px-4 py-2.5">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {instruments.filter(i => !wlSearch || i.symbol.toLowerCase().includes(wlSearch.toLowerCase())).map(inst => {
                  const tick   = getTickForSymbol(inst.symbol)
                  const ltp    = tick?.ltp
                  const chg    = tick?.change ?? 0
                  const chgPos = chg >= 0
                  const live   = tick != null

                  return (
                    <tr key={inst._id} className="hover:bg-gray-50 transition-colors group">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openTradingView(inst.symbol, inst.exchange)}
                          className="flex items-center gap-1 font-semibold text-primary-600 hover:text-primary-700"
                        >
                          {inst.symbol} <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                        </button>
                        <div className="text-xs text-gray-400">{inst.exchange}</div>
                      </td>

                      <td className={`px-4 py-3 text-right font-bold tabular-nums ${
                        !live ? 'text-gray-300' : chgPos ? 'text-success-700' : 'text-danger-700'
                      }`}>
                        {live ? `₹${fmt(ltp)}` : '—'}
                      </td>

                      {['open', 'high', 'low'].map(k => (
                        <td key={k} className={`px-4 py-3 text-right tabular-nums ${
                          k === 'high' ? 'text-success-600' : k === 'low' ? 'text-danger-600' : 'text-gray-600'
                        }`}>
                          {tick?.[k] != null ? `₹${fmt(tick[k])}` : '—'}
                        </td>
                      ))}

                      <td className={`px-4 py-3 text-right font-semibold tabular-nums ${
                        !live ? 'text-gray-300' : chgPos ? 'text-success-600' : 'text-danger-600'
                      }`}>
                        {live ? (
                          <span className="flex items-center justify-end gap-0.5">
                            {chgPos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {chgPos ? '+' : ''}{fmt(chg)}%
                          </span>
                        ) : '—'}
                      </td>

                      <td className="px-4 py-3 text-right text-gray-500 text-xs tabular-nums">
                        {tick?.volume != null ? Number(tick.volume).toLocaleString('en-IN') : '—'}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => removeInstrument(inst._id, inst.symbol)}
                          className="p-1.5 text-gray-300 hover:text-danger-600 hover:bg-danger-50 rounded transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Streaming
